import { Link, useLocation } from "react-router-dom";
import {
    ShieldCheck,
    Clock,
    Mail,
    ArrowLeft,
    UserCheck,
    Bell,
} from "lucide-react";

export default function AccessPending() {
    const loc = useLocation();
    const { email, name } = loc.state || {};

    return (
        <div className="min-h-screen grid lg:grid-cols-5 login-scope">
            <div className="hidden lg:flex lg:col-span-3 relative overflow-hidden text-white p-10 xl:p-14 flex-col login-hero">
                <div className="login-hero__grid" aria-hidden />
                <div className="login-hero__glow" aria-hidden />
                <div className="relative z-10 max-w-2xl mt-auto mb-auto">
                    <div className="login-brandmark" style={{ width: 60, height: 60, borderRadius: 16 }}>
                        <UserCheck size={28} strokeWidth={2} />
                    </div>
                    <h1 className="login-hero__title mt-6">
                        You&rsquo;re on the list.
                    </h1>
                    <p className="login-hero__tagline">
                        Access to financial data is admin-approved. Every time.
                    </p>
                    <div className="login-hero__accent-line" />
                    <div className="mt-8 max-w-md text-[13px] text-slate-300 leading-relaxed">
                        We&rsquo;ve notified the account owner. Once approved you&rsquo;ll be able to sign
                        in with Google straight away — no additional setup, and no password to remember.
                    </div>
                </div>
            </div>

            <div className="lg:col-span-2 flex items-center justify-center p-6 sm:p-10 bg-stone-50">
                <div className="w-full max-w-md bg-white rounded-2xl border border-stone-200/70 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.25)] p-8 sm:p-10 login-card" data-testid="access-pending-page">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="login-brandmark">
                            <ShieldCheck size={22} strokeWidth={2} />
                        </div>
                        <div className="leading-tight">
                            <div className="text-[15px] font-bold text-slate-900 tracking-[0.14em] leading-none">
                                CREDIT CARD
                            </div>
                            <div className="text-[13px] font-semibold text-slate-500 tracking-[0.14em] leading-none mt-1.5">
                                CONTROL CENTER
                            </div>
                        </div>
                    </div>

                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 mb-5">
                        <div className="flex items-start gap-3">
                            <Clock size={22} className="text-amber-600 mt-0.5 shrink-0" />
                            <div>
                                <div className="text-amber-900 font-semibold">Waiting for approval</div>
                                <div className="text-amber-800 text-sm mt-1 leading-relaxed">
                                    Thanks{name ? `, ${name}` : ""}. Your Google sign-in was received,
                                    and an administrator has been notified. You&rsquo;ll get access to
                                    the dashboard as soon as they approve your request.
                                </div>
                            </div>
                        </div>
                    </div>

                    {email && (
                        <div className="mb-5 text-sm rounded-lg border border-stone-200 bg-stone-50 px-3 py-2.5 flex items-center gap-2 text-stone-700">
                            <Mail size={14} className="text-stone-500" />
                            <span className="font-medium truncate" data-testid="access-pending-email">{email}</span>
                        </div>
                    )}

                    <div className="space-y-3 text-sm text-slate-600 leading-relaxed">
                        <div className="flex items-start gap-2.5">
                            <div className="w-6 h-6 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 mt-0.5 text-[11px] font-semibold">1</div>
                            <div>An admin sees your request in <span className="font-medium text-slate-800">Users → Pending approvals</span>.</div>
                        </div>
                        <div className="flex items-start gap-2.5">
                            <div className="w-6 h-6 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 mt-0.5 text-[11px] font-semibold">2</div>
                            <div>They pick your role — Viewer, Expense Entry, Finance Manager, or Admin.</div>
                        </div>
                        <div className="flex items-start gap-2.5">
                            <div className="w-6 h-6 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 mt-0.5 text-[11px] font-semibold">3</div>
                            <div className="inline-flex items-center gap-1.5">
                                Sign in again with Google
                                <Bell size={12} className="text-slate-400" />
                                <span className="text-slate-400 text-xs">— you&rsquo;ll be in.</span>
                            </div>
                        </div>
                    </div>

                    <div className="mt-6 pt-5 border-t border-slate-100">
                        <Link
                            to="/login"
                            className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium"
                            data-testid="access-pending-back-link"
                        >
                            <ArrowLeft size={14} /> Back to sign in
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
